import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { menus, PRIMARY_NAV } from "../../resources/menu";
import { MdDescription, MdSearch, MdTag, MdTaskAlt } from "react-icons/md";

/**
 * 命令面板能跳的頁面清單。
 *
 * 改版後側欄只剩 PRIMARY_NAV 那幾個入口，menus 裡大半項目都掛上了 hidden
 * （它們留著是給 routePermissionOf 找權限碼用的）。只讀 menus 的話命令面板會
 * 幾乎變空，連「今天」「工項」這些主要入口都搜不到 —— 那幾個入口本來就不在 menus 裡。
 *
 * 用 path 去重：「專案」（入口）與「專案列表」（menus）指向同一個網址，
 * 列兩次只是把真正想找的東西往下擠。hidden 的項目照舊不列，側欄看不到的頁面
 * 不該從這裡又冒出來。
 */
function navigablePages() {
  const pages = PRIMARY_NAV.map((n) => ({ label: n.label, path: n.path }));
  menus
    .filter((m) => m.type !== "group" && !m.hidden && m.path)
    .forEach((m) => {
      if (!pages.some((p) => p.path === m.path)) pages.push({ label: m.label, path: m.path });
    });
  return pages;
}

/**
 * 命令面板（Ctrl + K）— 搜尋範圍：專案、工項、頁面。
 *
 * 舊註解寫「逐字對應 DESIGN 稿 L874-L884」是錯的：那幾行是看板的卡片，設計稿裡沒有
 * 命令面板的 markup —— 它在 el-pm-template_4.html:2090、2590 反而把這個彈窗改成獨立的
 * 「搜尋」一頁。所以「這些尺寸是設計稿指定的」講不通，字級一律照通則走 --t-*：
 * 寫死 px 的話，切大字模式時整站放大、只有這個面板原地不動（README 規則 6）。
 *
 * MUI Dialog：點遮罩、按 ESC 都走 onClose。
 */
function CommandPalette({ open, onClose, projects, tasks }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const items = useMemo(() => {
    const go = (path) => () => {
      navigate(path);
      onClose();
    };
    return [
      ...projects.map((p) => ({
        icon: <MdTag />,
        label: p.name,
        sub: `專案 · ${p.code}`,
        onClick: go(`/Project/Detail/${p.id}`),
      })),
      ...tasks.slice(0, 8).map((t) => ({
        icon: <MdTaskAlt />,
        label: t.name,
        sub: `工項 · ${t.code}`,
        onClick: go(`/Project/Detail/${t.projectId}?task=${t.id}`),
      })),
      ...navigablePages().map((m) => ({
        icon: <MdDescription />,
        label: m.label,
        sub: "前往頁面",
        onClick: go(m.path),
      })),
    ];
  }, [projects, tasks, navigate, onClose]);

  const results = useMemo(() => {
    const cq = q.trim().toLowerCase();
    const src = cq ? items.filter((i) => `${i.label}${i.sub}`.toLowerCase().includes(cq)) : items;
    return src.slice(0, 8);
  }, [q, items]);

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      slotProps={{ paper: { sx: { mt: "12vh", overflow: "hidden" } } }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <TextField
          autoFocus
          fullWidth
          variant="standard"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜尋專案、工項、頁面…"
          slotProps={{
            input: {
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch size={18} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 700, color: "text.disabled", border: 1, borderColor: "divider", borderRadius: "var(--radius-xs)", px: 0.75 }}
                  >
                    ESC
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={{ maxHeight: "52vh", overflow: "auto", p: 1 }}>
        {results.length === 0 ? (
          <Typography sx={{ p: 3.25, textAlign: "center", color: "text.disabled", fontWeight: 600 }}>找不到符合的項目</Typography>
        ) : (
          <List disablePadding>
            {results.map((r, i) => (
              <ListItemButton key={`${r.label}-${i}`} onClick={r.onClick} sx={{ borderRadius: "var(--radius-sm)", gap: 1.5 }}>
                <ListItemAvatar sx={{ minWidth: 0 }}>
                  <Avatar variant="rounded" sx={{ bgcolor: "action.hover", color: "primary.main", width: 36, height: 36 }}>
                    {r.icon}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={r.label}
                  secondary={r.sub}
                  slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                  sx={{ minWidth: 0 }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Dialog>
  );
}

CommandPalette.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  projects: PropTypes.array,
  tasks: PropTypes.array,
};

CommandPalette.defaultProps = {
  open: false,
  onClose: () => {},
  projects: [],
  tasks: [],
};

export default CommandPalette;
